import { animalSpeciesHelper } from '@farmshare/utils';
import { NavigateNext } from '@mui/icons-material';
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Link as MuiLink,
  Slide,
  Stack,
  Typography,
} from '@mui/material';
import { map } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  EnumProcessorSchedulingStatus,
  useProducerJobByIdQuery,
} from 'lib/graphql';

import { completedStatuses } from 'pages/processor/dashboard/filters';

import { STATUS_DISPLAY } from '../../shared/styles';
import { HeadSplitSection } from './head-split-section/head-split-section';
import { JobInfoCard } from './job-info-card';

export function ProducerJobDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, loading } = useProducerJobByIdQuery({
    variables: { id: id! },
    skip: !id,
  });

  const job = data?.processorSchedulingById;

  const title = useMemo(() => {
    if (!job) return '';
    const processor = job.vendor?.shop_name ?? '';
    const species = animalSpeciesHelper(job.animalSpecies).label;
    const dropoff = job.dropoffDate
      ? moment(job.dropoffDate).utc().format('MMM D, YYYY')
      : '';
    return [processor, `${job.headCount} ${species}`, dropoff]
      .filter(Boolean)
      .join(' | ');
  }, [job]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Stack sx={{ p: 2 }} gap={2}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
          <MuiLink
            underline="hover"
            color="inherit"
            component={Link}
            to="/dashboard"
            fontWeight="normal"
          >
            Dashboard
          </MuiLink>
          <Typography color="text.primary">Job not found</Typography>
        </Breadcrumbs>
        <Typography color="text.secondary">Job not found.</Typography>
      </Stack>
    );
  }

  return (
    <Slide direction="left" in mountOnEnter timeout={250}>
      <Stack sx={{ p: 2, pt: 4 }} gap={2}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
          <MuiLink
            underline="hover"
            color="inherit"
            component={Link}
            to="/dashboard"
            fontWeight="normal"
          >
            All Jobs
          </MuiLink>
          <Typography color="text.primary">{title}</Typography>
        </Breadcrumbs>

        {/* Job info card */}
        <JobInfoCard job={job} />

        {job.status === EnumProcessorSchedulingStatus.Requested && (
          <Alert severity="info">
            You can assign splits, buyers, and cutsheets after the job has been
            accepted by {job.vendor?.shop_name ?? 'the processor'}.
          </Alert>
        )}

        {completedStatuses.includes(
          job.status as EnumProcessorSchedulingStatus,
        ) && (
          <Alert severity="warning">
            This job is {STATUS_DISPLAY[job.status as string] ?? job.status}.
            Heads cannot be edited.
          </Alert>
        )}

        {map(job.animalHeads, (head, idx) =>
          head ? (
            <HeadSplitSection
              key={head._id ?? idx}
              head={head}
              index={idx}
              job={job}
              readOnly={completedStatuses.includes(
                job.status as EnumProcessorSchedulingStatus,
              )}
            />
          ) : null,
        )}
      </Stack>
    </Slide>
  );
}
